// Where a chat pane sits when a message lands.
//
// TWO POLICIES, because two kinds of conversation want opposite things:
//
//   anchor: "top"    — an LLM chat. The message you just sent is pinned to the
//                      TOP of the pane and the answer grows downward into the
//                      space below it. This is what Claude and ChatGPT do, and
//                      the reason is long answers: bottom-anchoring makes the
//                      viewport chase the tail of a streaming reply, so you are
//                      permanently reading its last line and have to scroll UP
//                      to read it from the start. Pinning the prompt puts the
//                      beginning of the answer where your eyes already are.
//
//   anchor: "bottom" — a human chat. Messages are short, so the newest one IS
//                      the bottom. Pinning a one-line "haha" to the top would
//                      leave a screen of blank underneath it.
//
// Both policies share everything else, which is the part every chat surface in
// this app was missing: the pane stops following the moment YOU scroll away
// (nothing yanks you back while you are reading history), it re-engages when
// you return to the bottom, and while it is disengaged a jump-to-latest button
// says so and takes you back.
//
// The spacer is what makes "top" possible at all. A scroller can only scroll to
// scrollHeight - clientHeight, so on a short thread — or before a reply has
// arrived — the newest message physically cannot reach the top. So we append an
// empty block after the content and give it exactly the height the anchor needs,
// no more, and re-measure as the reply grows. Same trick both apps use.
//
// See also utils/thread_scroll.js, which pins EMAIL threads (a different shape:
// entries taller than the pane, no streaming, no re-engagement).

const DEFAULTS = {
    // How close to the bottom still counts as "following". Generous enough to
    // survive a rounding wobble or a late image, small enough that a deliberate
    // scroll of one message disengages.
    threshold: 96,
    // Breathing room above a top-anchored message so it isn't flush to the edge.
    gap: 12,
    // How long to keep re-pinning through late layout (images, avatars and web
    // fonts all resolve after the swap and grow the content underneath us).
    settle: 1500
}

export function attachChatScroll(scroller, options = {}) {
    if (!scroller) return nullHandle()

    const config = { ...DEFAULTS, ...options }
    const state = {
        engaged: true,
        // The last position WE wrote, so the scroll event our own write echoes
        // back doesn't read as the user scrolling away. A position, not a
        // "programmatic" boolean cleared on the next frame: that flag also
        // swallowed REAL user scrolls that happened to land in the same frame as
        // a re-pin, and since the settle ResizeObserver can fire anywhere in its
        // window that collision was routine — the pane kept thinking it was
        // still following and pinned the reader back where they had scrolled
        // away from.
        lastWrittenTop: null,
        anchorEl: null,
        destroyed: false
    }

    const spacer = config.anchor === "top" ? ensureSpacer(scroller) : null
    const jumpButton = config.jumpButton === false ? null : ensureJumpButton(scroller)

    const onScroll = () => {
        if (state.destroyed) return
        if (state.lastWrittenTop !== null && Math.abs(scroller.scrollTop - state.lastWrittenTop) <= 1) return
        // The two policies disengage differently, and conflating them is a bug
        // worth naming: a top-anchored pane pins ONE message, so "am I still
        // following?" cannot mean "am I at the bottom" — scrolling down to read
        // the end of a long answer would then re-engage and the next streaming
        // re-render would yank the reader back up to the prompt. So a top pin is
        // one-shot: any deliberate scroll ends it, and only the next send (or
        // thread load) re-arms it via reset().
        //
        // A bottom-anchored pane is the classic case and does mean exactly
        // that: scroll up to read history and it stops following, come back to
        // the bottom and it resumes.
        state.engaged = config.anchor === "top" ? false : atBottom(scroller, config.threshold)
        syncJumpButton()
    }

    const syncJumpButton = () => {
        if (!jumpButton) return
        // Offer the button whenever there is something below the fold — which
        // for a top-anchored pane includes a reply still growing past it.
        const hasMore = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight > config.threshold
        jumpButton.classList.toggle("is-visible", hasMore)
    }

    const applyScrollTop = (top) => {
        state.lastWrittenTop = Math.round(top)
        scroller.scrollTop = top
        syncJumpButton()
    }

    // Pin per policy. force ignores disengagement — used on first paint and by
    // the jump button, never by a background re-render.
    const pin = ({ force = false } = {}) => {
        if (state.destroyed) return
        const following = force || state.engaged

        if (config.anchor === "top" && state.anchorEl?.isConnected) {
            // Touch the spacer ONLY while we are still the one driving the
            // scroll. Resizing it under a reader who has scrolled shrinks
            // scrollHeight, the browser clamps their scrollTop to the smaller
            // maximum, and growing it back does not give the position back —
            // measured as a 259px unrequested jump on every poll tick.
            const top = sizeSpacerForAnchor(scroller, spacer, state.anchorEl, config.gap, { adjust: following })
            // Skip a redundant write: re-asserting a position we are already at
            // is invisible at best and a source of jitter at worst.
            if (following && Math.abs(scroller.scrollTop - top) > 1) applyScrollTop(top)
            else syncJumpButton()
            return
        }

        if (!following) { syncJumpButton(); return }
        if (spacer) spacer.style.height = "0px"
        applyScrollTop(Math.max(0, scroller.scrollHeight - scroller.clientHeight))
    }

    // Re-pin through late layout without fighting a reader who has scrolled.
    let settleObserver = null
    let settleTimer = null
    const stopSettling = () => {
        settleObserver?.disconnect()
        settleObserver = null
        clearTimeout(settleTimer)
        settleTimer = null
    }
    const settle = () => {
        stopSettling()
        if (typeof ResizeObserver !== "function") return

        settleObserver = new ResizeObserver(() => {
            if (state.destroyed) return
            pin()
        })
        // The scroller's own box is fixed by the flex layout; it's the content
        // inside that grows as images and fonts resolve.
        Array.from(scroller.children).forEach((child) => settleObserver.observe(child))
        settleTimer = setTimeout(stopSettling, config.settle)
    }

    scroller.addEventListener("scroll", onScroll, { passive: true })
    if (jumpButton) {
        jumpButton.addEventListener("click", () => {
            state.engaged = true
            // Always land at the true bottom: the button's promise is "show me
            // the newest thing", not "re-apply the anchor".
            if (spacer) spacer.style.height = "0px"
            applyScrollTop(Math.max(0, scroller.scrollHeight - scroller.clientHeight))
        })
    }

    return {
        // Point a top-anchored pane at the message to pin. Ignored by a
        // bottom-anchored one, which always wants the end.
        anchorTo(element) {
            state.anchorEl = element || null
            return this
        },
        get anchored() { return !!state.anchorEl },
        // First paint, or any moment the caller knows the pane should reset
        // regardless of where the reader had scrolled to.
        reset() {
            state.engaged = true
            pin({ force: true })
            settle()
            return this
        },
        // A re-render or a streaming flush landed. Honours disengagement.
        contentChanged() {
            pin()
            settle()
            return this
        },
        get engaged() { return state.engaged },
        destroy() {
            state.destroyed = true
            stopSettling()
            scroller.removeEventListener("scroll", onScroll)
        }
    }
}

// One-liner for the human chats, which all have the same shape: a controller
// that re-renders a message pane in place and wants it to stay at the bottom.
// Attaches on first use, reuses the handle after that, and re-attaches if the
// pane element itself was replaced. `owner` is the controller instance.
export function followBottom(owner, scroller) {
    if (!scroller) return nullHandle()
    if (owner._chatScroll && owner._chatScrollFor === scroller) return owner._chatScroll

    owner._chatScroll?.destroy()
    owner._chatScrollFor = scroller
    owner._chatScroll = attachChatScroll(scroller, { anchor: "bottom" })
    return owner._chatScroll
}

// Height the spacer needs for `anchorEl` to reach the top, and the scrollTop
// that puts it there.
//
// Measured WITHOUT mutating layout first. The obvious implementation collapses
// the spacer to find the real content height and then grows it back, and that
// is a bug, not an optimisation: between the two writes the scroller's maximum
// scrollTop drops by the spacer's height, so the browser clamps the current
// position to fit and nothing restores it afterwards. Subtracting the spacer's
// own height from scrollHeight answers the same question and moves nothing.
//
// anchorTop is invariant under scrolling (rect delta plus scrollTop), so it is
// safe to read at any position.
function sizeSpacerForAnchor(scroller, spacer, anchorEl, gap, { adjust = true } = {}) {
    const spacerHeight = spacer ? spacer.offsetHeight : 0
    const anchorTop = anchorEl.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop
    const target = Math.max(0, anchorTop - gap)

    if (spacer && adjust) {
        const contentMax = Math.max(0, scroller.scrollHeight - spacerHeight - scroller.clientHeight)
        const shortfall = target - contentMax
        spacer.style.height = shortfall > 0 ? `${Math.ceil(shortfall)}px` : "0px"
    }

    return target
}

function atBottom(scroller, threshold) {
    return scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= threshold
}

function ensureSpacer(scroller) {
    let spacer = scroller.querySelector(":scope > [data-chat-spacer]")
    if (spacer) return spacer

    spacer = document.createElement("div")
    spacer.setAttribute("data-chat-spacer", "")
    spacer.setAttribute("aria-hidden", "true")
    spacer.style.flex = "0 0 auto"
    scroller.appendChild(spacer)
    return spacer
}

// The button lives inside the scroller's offset parent rather than the scroller
// itself, so it doesn't scroll away with the content.
function ensureJumpButton(scroller) {
    const host = scroller.parentElement
    if (!host) return null

    let button = host.querySelector(":scope > [data-chat-jump]")
    if (button) return button

    // The button is absolutely positioned against this host. Establish the
    // containing block here rather than in CSS, so any pane can host one
    // without every layout having to know about it.
    if (getComputedStyle(host).position === "static") host.style.position = "relative"

    button = document.createElement("button")
    button.type = "button"
    button.className = "chat-jump-latest"
    button.setAttribute("data-chat-jump", "")
    button.setAttribute("aria-label", "Jump to latest")
    button.innerHTML = '<i class="bi bi-arrow-down" aria-hidden="true"></i>'
    host.appendChild(button)
    return button
}

function nullHandle() {
    return {
        anchorTo() { return this },
        reset() { return this },
        contentChanged() { return this },
        engaged: true,
        destroy() {}
    }
}
